import { AUCTION_SCHEDULE_TIME_ZONE, type AuctionSession, type AuctionSessionBidLot } from '@carsfinders/contracts';

import type {
  ScheduleCalendarCell,
  ScheduleDayGroup,
  ScheduleSessionView,
  SessionStatus,
} from './session-schedule-types';

/**
 * Pure date/grouping helpers behind the admin's Design D session schedule tabs
 * — a port of `apps/auction/src/features/auction-schedule/utils/scheduleSessions.ts`.
 *
 * Every calendar-day computation is done in UTC on `YYYY-MM-DD` strings, never
 * with local `Date` accessors, for the same reason the web original documents:
 * the server has already resolved each session's `date`/`time` into
 * Europe/Kyiv, so re-deriving a weekday with `getDay()` would re-apply the
 * VIEWER's offset on top of a value that is already zoned.
 *
 * Locale is fixed to `uk-UA` throughout — the admin SPA has no locale
 * switcher (`apps/admin/src/lib/i18n.ts`) and every other admin component
 * formats dates the same way (see e.g. `../list-row.tsx`).
 */

const LOCALE = 'uk-UA';

/** Today's calendar day in the schedule's display zone, `YYYY-MM-DD`. */
export function todayInDisplayZone(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: AUCTION_SCHEDULE_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** `YYYY-MM-DD` shifted by whole days, UTC-anchored so no DST edge can shift it. */
export function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Whole calendar days from `fromIso` to `toIso`; negative when `toIso` is earlier. */
export function dayDiff(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) / 86_400_000);
}

/**
 * The 7-column month grid: leading days from the previous month, the month
 * itself, then enough trailing days to fill the last week. Weeks start Monday.
 */
export function buildMonthGrid(year: number, monthIndex: number): ScheduleCalendarCell[] {
  const first = new Date(Date.UTC(year, monthIndex, 1));
  const lead = (first.getUTCDay() + 6) % 7;
  const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const cells: ScheduleCalendarCell[] = [];

  for (let index = 0; index < lead; index += 1) {
    const date = new Date(Date.UTC(year, monthIndex, index + 1 - lead));
    cells.push({ date: date.toISOString().slice(0, 10), day: date.getUTCDate(), outside: true });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(Date.UTC(year, monthIndex, day));
    cells.push({ date: date.toISOString().slice(0, 10), day, outside: false });
  }

  while (cells.length % 7 !== 0) {
    const date = new Date(Date.UTC(year, monthIndex + 1, cells.length - lead - daysInMonth + 1));
    cells.push({ date: date.toISOString().slice(0, 10), day: date.getUTCDate(), outside: true });
  }

  return cells;
}

/** Fourteen consecutive days starting at `startIso` — the timeline's columns. */
export function buildTimelineDays(startIso: string, length = 14): string[] {
  return Array.from({ length }, (_, index) => addDays(startIso, index));
}

/**
 * The window the screen has to fetch: the visible month's whole grid, widened
 * to always contain today and the timeline's fourteen days — `stats` is
 * computed server-side over the requested window.
 */
export function sessionWindow(year: number, monthIndex: number, today: string): { from: string; to: string } {
  const grid = buildMonthGrid(year, monthIndex);
  const gridFrom = grid[0]!.date;
  const gridTo = grid[grid.length - 1]!.date;
  const timelineTo = addDays(today, 13);

  return {
    from: gridFrom < today ? gridFrom : today,
    to: gridTo > timelineTo ? gridTo : timelineTo,
  };
}

/**
 * Where a session sits relative to now. `past` is decided by `endsAt`, not by
 * the calendar day: a session whose first lot closed an hour ago may still
 * have lots live.
 */
export function sessionStatus(session: AuctionSession, today: string, now: Date = new Date()): SessionStatus {
  if (new Date(session.endsAt).getTime() < now.getTime()) return 'past';

  const offset = dayDiff(today, session.date);
  if (offset < 0) return 'past';
  return offset <= 1 ? 'soon' : 'upcoming';
}

/**
 * Deterministic colour ramp for auction houses, assigned by their index in the
 * response's `houses` array — see the web original for why the live data
 * currently yields exactly one house.
 */
const HOUSE_COLORS = [
  'var(--color-cf-ink-muted)',
  'var(--color-cf-ink-soft)',
  'var(--color-cf-ink-subtle)',
  'var(--color-cf-ink-faint)',
] as const;

export function houseColor(index: number): string {
  return HOUSE_COLORS[((index % HOUSE_COLORS.length) + HOUSE_COLORS.length) % HOUSE_COLORS.length]!;
}

/**
 * Colour of the square marker next to a session. Urgency beats house.
 *
 * Deliberately NOT bid-aware — the web original's `sessionDotColor` gives
 * `session.myBids > 0` the first say; the admin operator's view has no "your
 * bids" concept, so that branch is dropped rather than ported dead.
 */
export function sessionDotColor(status: SessionStatus, fallback: string): string {
  if (status === 'soon') return 'var(--color-cf-accent)';
  if (status === 'past') return 'var(--color-cf-past)';
  return fallback;
}

/**
 * Background + text pair for a session chip. Same omission as
 * `sessionDotColor`: no bid-state branch.
 */
export function sessionChipClasses(status: SessionStatus): string {
  if (status === 'soon') return 'bg-cf-accent/16 text-cf-ink';
  if (status === 'past') return 'bg-cf-ground text-cf-ink-soft';
  return 'bg-cf-fill text-cf-ink';
}

/** Decorated sessions bucketed by calendar day, days ascending, each day in closing order. */
export function groupByDay(views: readonly ScheduleSessionView[]): ScheduleDayGroup[] {
  const byDay = new Map<string, ScheduleSessionView[]>();

  for (const view of views) {
    const bucket = byDay.get(view.session.date);
    if (bucket) bucket.push(view);
    else byDay.set(view.session.date, [view]);
  }

  return Array.from(byDay.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([date, dayViews]) => ({
      date,
      sessions: [...dayViews].sort((left, right) => left.session.time.localeCompare(right.session.time)),
      lots: dayViews.reduce((total, view) => total + view.session.lots, 0),
    }));
}

/** Decorated sessions closing on one calendar day, in closing order. */
export function viewsOnDay(views: readonly ScheduleSessionView[], date: string): ScheduleSessionView[] {
  return views
    .filter((view) => view.session.date === date)
    .sort((left, right) => left.session.time.localeCompare(right.session.time));
}

/* ── labels (uk-UA, fixed locale) ─────────────────────────────────────────── */

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** «Вересень 2026» — Ukrainian months come back lowercase from ICU. */
export function formatMonthLabel(year: number, monthIndex: number): string {
  const label = new Intl.DateTimeFormat(LOCALE, {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(new Date(Date.UTC(year, monthIndex, 1)));

  return capitalize(label);
}

/** «16 вересня» — the genitive form the artboard prints. */
export function formatDayMonth(iso: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
  }).format(new Date(`${iso}T00:00:00Z`));
}

/** «Ср» — the two/three-letter weekday used in headers and day tags. */
export function formatWeekdayShort(iso: string): string {
  const label = new Intl.DateTimeFormat(LOCALE, {
    timeZone: 'UTC',
    weekday: 'short',
  }).format(new Date(`${iso}T00:00:00Z`));

  return capitalize(label.replace(/\.$/, ''));
}

/** Monday-first weekday headers for the calendar grid. */
export function weekdayHeadings(): string[] {
  // 2026-01-05 is a Monday; any Monday would do.
  return Array.from({ length: 7 }, (_, index) => formatWeekdayShort(addDays('2026-01-05', index)));
}

/** «16 вересня, Ср» — the day panel's heading. */
export function formatPanelTitle(iso: string): string {
  return `${formatDayMonth(iso)}, ${formatWeekdayShort(iso)}`;
}

/** «Ср, 16 вересня» — the list view's day heading. */
export function formatListDayTitle(iso: string): string {
  return `${formatWeekdayShort(iso)}, ${formatDayMonth(iso)}`;
}

/** Wall clock `HH:mm` of an instant in the display zone. */
export function formatClock(instant: Date): string {
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: AUCTION_SCHEDULE_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(instant);
}

/**
 * Plural category for a count, as a translation-key suffix. Ukrainian has
 * three (1 аукціон / 2 аукціони / 5 аукціонів).
 */
export function pluralSuffix(count: number): 'one' | 'few' | 'many' {
  const category = new Intl.PluralRules(LOCALE).select(count);
  if (category === 'one') return 'one';
  if (category === 'few') return 'few';
  return 'many';
}

/* ── shared copy builders ─────────────────────────────────────────────────── */

type Translate = (_key: string, _params?: Record<string, string | number>) => string;

/** «42 лоти». */
export function lotsLabel(count: number, t: Translate): string {
  return `${count} ${t(`auctionSchedule.units.lots.${pluralSuffix(count)}`)}`;
}

/** «3 ставки». */
export function bidsLabel(count: number, t: Translate): string {
  return `${count} ${t(`auctionSchedule.units.bids.${pluralSuffix(count)}`)}`;
}

/** A bid lot's name: make/model/year, else «Лот <listing id | car id>». */
export function bidLotTitle(lot: AuctionSessionBidLot, t: Translate): string {
  return lot.title ?? t('auctionSchedule.bidLots.untitled', { id: lot.listingId ?? lot.carId });
}

/** «лідер: Іван» / «клієнта не знайдено». */
export function bidLotLeaderLine(lot: AuctionSessionBidLot, t: Translate): string {
  return lot.leader.label
    ? t('auctionSchedule.bidLots.leader', { name: lot.leader.label })
    : t('auctionSchedule.bidLots.leaderUnknown');
}

/** «3 аукціони». */
export function sessionsLabel(count: number, t: Translate): string {
  return `${count} ${t(`auctionSchedule.units.sessions.${pluralSuffix(count)}`)}`;
}

/** «завершено» / «закривається сьогодні» / «закриття через 4 дн.» */
export function statusLabel(view: ScheduleSessionView, today: string, t: Translate): string {
  if (view.status === 'past') return t('auctionSchedule.session.finished');

  const offset = dayDiff(today, view.session.date);
  if (offset <= 0) return t('auctionSchedule.session.closingToday');
  if (offset === 1) return t('auctionSchedule.session.closingTomorrow');
  return t('auctionSchedule.session.closingInDays', { days: offset });
}

/** The list view's right-hand column: «—» / «сьогодні» / «завтра» / «4 дн.» */
export function countdownLabel(view: ScheduleSessionView, today: string, t: Translate): string {
  const offset = dayDiff(today, view.session.date);
  if (view.status === 'past' || offset < 0) return '—';
  if (offset === 0) return t('auctionSchedule.session.today');
  if (offset === 1) return t('auctionSchedule.session.tomorrow');
  return t('auctionSchedule.session.days', { days: offset });
}

/** True while the countdown should read in the artboard's amber, not grey. */
export function isCountdownUrgent(view: ScheduleSessionView, today: string): boolean {
  const offset = dayDiff(today, view.session.date);
  return view.status !== 'past' && offset >= 0 && offset <= 1;
}
